import type { QuestionLearner } from '~/gql/questions/questions'
import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { SidePanel } from '~/components/SidePanel'
import Spinner from '~/components/Spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { GQL_LEARNER_MODULE_ATTEMPTS } from '~/gql/questions/questions'
import { userDisplayName } from '~/helpers/users/display-name'
import { formatScore } from '~/pages/backoffice/admin/statistics/score-format'
import { completionLabelKey, successLabelKey } from '~/pages/backoffice/admin/statistics/status-labels'
import { expectedAnswer } from './question-display'

type Attempt = {
  sessionId: string
  sessionName: string
  number: number
  completionStatus: string
  successStatus: string
  scoreRaw?: number | null
  scoreMax?: number | null
  updatedAt: unknown
  interactions: { index: number, interactionId: string, description?: string | null, correctResponses: string[], learnerResponse?: string | null, result?: string | null }[]
}

type Props = {
  learner: QuestionLearner | null
  moduleId: string
  sessionId: string
  onClose: () => void
}

/** Les réponses d'un apprenant à un module, tentative par tentative (#84, #159). */
function LearnerAnswersPanel({ learner, moduleId, sessionId, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const { data, loading, error } = useQuery(GQL_LEARNER_MODULE_ATTEMPTS, {
    variables: { input: { userId: learner?.id ?? '', moduleId, ...(sessionId && { sessionId }) } },
    skip: !learner,
  })
  const attempts = (data?.learnerModuleAttempts ?? []) as Attempt[]

  const body = () => {
    if (loading) return <div className="flex justify-center p-10"><Spinner className="size-6" /></div>
    if (error) return <p role="alert" className="text-sm text-destructive">{t('questions.learner.loadError')}</p>
    if (attempts.length === 0) return <p className="text-sm text-muted-foreground">{t('questions.learner.empty')}</p>
    return attempts.map(attempt => (
      <section key={`${attempt.sessionId}-${attempt.number}`} className="flex flex-col gap-2" aria-labelledby={`attempt-${attempt.sessionId}-${attempt.number}`}>
        <h3 id={`attempt-${attempt.sessionId}-${attempt.number}`} className="text-sm font-semibold">
          {t('questions.learner.attempt', { number: attempt.number, path: attempt.sessionName })}
        </h3>
        <p className="text-xs text-muted-foreground">
          {[
            t(completionLabelKey(attempt.completionStatus)),
            t(successLabelKey(attempt.successStatus)),
            formatScore(attempt.scoreRaw, attempt.scoreMax),
            new Date(attempt.updatedAt as string).toLocaleDateString(i18n.language),
          ].filter(Boolean).join(' · ')}
        </p>
        {attempt.interactions.length === 0
          ? <p className="text-sm text-muted-foreground">{t('questions.learner.noAnswer')}</p>
          : (
              <Table aria-label={t('questions.learner.attempt', { number: attempt.number, path: attempt.sessionName })}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('questions.column.question')}</TableHead>
                    <TableHead>{t('questions.column.expected')}</TableHead>
                    <TableHead>{t('questions.learner.answer')}</TableHead>
                    <TableHead>{t('questions.learner.result')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attempt.interactions.map(interaction => (
                    <TableRow key={interaction.index}>
                      <TableCell className="max-w-xs whitespace-normal">{interaction.description ?? interaction.interactionId}</TableCell>
                      <TableCell className="whitespace-normal font-mono text-xs">{expectedAnswer(interaction.correctResponses) || '—'}</TableCell>
                      <TableCell className="whitespace-normal font-mono text-xs">{interaction.learnerResponse ?? '—'}</TableCell>
                      <TableCell>{interaction.result ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </section>
    ))
  }

  return (
    <SidePanel
      open={learner != null}
      onOpenChange={open => !open && onClose()}
      size="lg"
      title={t('questions.learner.title')}
      description={learner ? userDisplayName(learner) : undefined}
    >
      <div className="flex flex-col gap-6">{body()}</div>
    </SidePanel>
  )
}

export default LearnerAnswersPanel
